/**
 * Solidarity News — shared source.
 *
 * Data flow:
 *   - Primary: fetched from backend `/api/solidarity-news` (admin-managed via QR Tools CMS)
 *   - Fallback: hardcoded entries below if API empty or unreachable
 *   - Latest entry id cached in localStorage so the navbar badge can read it synchronously
 *
 * Newest entry goes FIRST in the list returned by the API (sorted by created_at desc).
 */

import { API } from "./api";

const SEEN_KEY = "solidarity_news_seen_id";
const LATEST_CACHE_KEY = "solidarity_news_latest_id";

/** Hardcoded fallback so the LIRR roundup keeps working even if the API is empty */
const FALLBACK_NEWS = [
  {
    id: "lirr-2026-05",
    headline: "LIRR Bargaining Coalition Strike",
    date: "May 17, 2026",
    summary:
      "3,500 Long Island Rail Road workers walked out after the MTA CEO rejected federal mediation. Coalition: BLET · TCU · IAM · IBEW · BRS.",
    coalition: ["BLET", "TCU", "IAM", "IBEW", "BRS"],
    links: [
      { label: "LIRR Bargaining Coalition Update (5/17)",  url: "https://www.tcunion.org/tcu-union/long-island-bargaining-coalition-update/", kind: "update" },
      { label: "Teamsters / BLET Statement",                url: "https://teamster.org/2026/05/3500-workers-walk-out-as-blet-teamsters-union-coalition-launch-lirr-strike/", kind: "statement" },
      { label: "TCU / IAM Statement",                       url: "https://www.tcunion.org/news/lirr-on-strike-no-deal-reached/", kind: "statement" },
      { label: "BRS Statement",                             url: "https://www.brs.org/?zone=%2Funionactive%2Fview_article.cfm&HomeID=995724", kind: "statement" },
      { label: "IAM Union Statement",                       url: "https://www.goiam.org/news/iam-union-tcu-iam-mta-ceo-janno-liebers-rejections-of-federal-panel-to-blame-for-impending-lirr-commuter-nightmare/", kind: "statement" },
      { label: "AFA-CWA Statement",                         url: "https://afacwa.org/stand-with-lirr-workers-strike/", kind: "statement" },
      { label: "AFL-CIO post (X)",                          url: "https://x.com/AFLCIO/status/2056042938992308298?s=20", kind: "social" },
      { label: "ATU post (X)",                              url: "https://x.com/ATUComm/status/2055665027978231925", kind: "social" },
      { label: "Transport Workers Union post (X)",          url: "https://x.com/transportworker/status/2055698310531285312?s=20", kind: "social" },
    ],
  },
];

/** Async fetch — merges API entries with the hardcoded fallback */
export const fetchSolidarityNews = async () => {
  try {
    const res = await fetch(`${API}/solidarity-news`);
    if (!res.ok) throw new Error("api error");
    const data = await res.json();
    const apiItems = data.items || [];
    const merged = [...apiItems, ...FALLBACK_NEWS];
    // Cache latest id for the navbar badge
    if (merged[0]?.id) {
      try { localStorage.setItem(LATEST_CACHE_KEY, merged[0].id); } catch {}
    }
    return merged;
  } catch {
    if (FALLBACK_NEWS[0]?.id) {
      try { localStorage.setItem(LATEST_CACHE_KEY, FALLBACK_NEWS[0].id); } catch {}
    }
    return FALLBACK_NEWS;
  }
};

/** Synchronous — used by the navbar badge. Returns the latest id we know about. */
export const getLatestSolidarityId = () => {
  try {
    return localStorage.getItem(LATEST_CACHE_KEY) || FALLBACK_NEWS[0]?.id || null;
  } catch {
    return FALLBACK_NEWS[0]?.id || null;
  }
};

/** True if there's unseen solidarity news */
export const hasUnseenSolidarityNews = () => {
  const latest = getLatestSolidarityId();
  if (!latest) return false;
  try {
    return localStorage.getItem(SEEN_KEY) !== latest;
  } catch {
    return true;
  }
};

/** Mark current latest as seen (call when /strikes is visited) */
export const markSolidarityNewsSeen = () => {
  const latest = getLatestSolidarityId();
  if (!latest) return;
  try { localStorage.setItem(SEEN_KEY, latest); } catch {}
};

/** Backwards-compat: legacy synchronous export still used by older imports.
 *  Returns the hardcoded fallback. Pages should prefer fetchSolidarityNews(). */
export const SOLIDARITY_NEWS = FALLBACK_NEWS;
